import React from 'react';
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  Radar as RadarIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export default function StockPhasesHeader({
  summary,
  tickerQuery,
  onTickerQueryChange,
  onSearchSubmit,
  isRefreshing,
  onRefresh,
}) {
  const { t } = useTranslation();

  return (
    <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          boxShadow: '0 3px 10px rgba(102,126,234,0.25)',
        }}>
          <RadarIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
              {t('phase.title')}
            </Typography>
            {summary?.date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#764ba2', boxShadow: '0 0 6px #764ba2' }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                  {summary.date}
                </Typography>
              </Box>
            )}
          </Box>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
            {t('phase.subtitle', { total: summary?.total || 0 })}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('phase.searchPlaceholder')}
          value={tickerQuery}
          onChange={(e) => onTickerQueryChange(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: 130, sm: 160 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              backgroundColor: '#fff',
              fontSize: '0.82rem',
              fontFamily: 'monospace',
              fontWeight: 600,
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
              '&.Mui-focused fieldset': { borderColor: '#667eea' },
            },
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={onRefresh}
          disabled={isRefreshing}
          startIcon={<RefreshIcon sx={{
            fontSize: 16,
            ...(isRefreshing && {
              animation: 'spin 1s linear infinite',
              '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
            }),
          }} />}
          sx={{
            borderRadius: '4px',
            borderColor: isRefreshing ? '#667eea' : '#e2e8f0',
            color: isRefreshing ? '#667eea' : '#64748b',
            fontWeight: 600,
            fontSize: 13,
            textTransform: 'none',
            px: 2,
            '&:hover': {
              borderColor: '#667eea',
              color: '#667eea',
              backgroundColor: 'rgba(102,126,234,0.04)',
            },
          }}
        >
          {isRefreshing ? t('phase.refreshing') : t('phase.refreshBtn')}
        </Button>
      </Box>
    </Box>
  );
}